import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { initialize } from '@pendo/web-sdk';
import { apiScopes, authService, isAuthDisabled, type AuthState } from '@/auth';
import { env } from '@/config/env';

type PendoVisitor = {
    id: string;
    email?: string;
    name?: string;
    givenName?: string;
    familyName?: string;
    locale?: string;
    isTenantAdmin?: boolean;
};

type PendoAccount = {
    id: string;
    name?: string;
    status?: string;
    applications?: PendoEntity[];
    contentFeatures?: PendoEntity[];
    permission?: string;
    createdDate?: string;
};

type PendoEntity = {
    id: string;
    name?: string;
};

type PendoIdentity = {
    visitor: PendoVisitor;
    account: PendoAccount;
};

type PendoContextResponse = {
    visitor?: Record<string, unknown>;
    account?: Record<string, unknown>;
    currentUser?: Record<string, unknown>;
    tenantContext?: Record<string, unknown>;
};

type ClaimEntry = {
    type?: unknown;
    value?: unknown;
};

type NamedEntity = {
    id?: unknown;
    name?: unknown;
};

type PendoAgent = {
    identify: (options: PendoIdentity) => void;
    teardown?: () => void;
};

type PendoProviderProps = {
    authState: AuthState;
    children: ReactNode;
};

type PendoAuthState = Pick<AuthState, 'isAuthenticated' | 'account'>;

type ContextState = {
    identity: PendoIdentity | null;
    error: Error | null;
};

function formatResponseSnippet(body: string): string {
    return body.replace(/\s+/g, ' ').trim().slice(0, 160);
}

async function parsePendoContextResponse(response: Response, contextUrl: string): Promise<PendoContextResponse> {
    const contentType = response.headers.get('content-type')?.toLowerCase() ?? '';
    const body = await response.text();

    if (!body.trim()) {
        throw new Error(`Pendo context endpoint returned an empty response from ${contextUrl}.`);
    }

    const looksLikeJson = contentType.includes('application/json') || /^[[{]/.test(body.trim());

    if (!looksLikeJson) {
        const snippet = formatResponseSnippet(body);
        throw new Error(
            `Pendo context endpoint returned non-JSON content from ${contextUrl} (${contentType || 'unknown content type'}). ` +
                `This usually means the URL is pointing at an HTML page, SPA fallback, or auth redirect. Response preview: ${snippet}`
        );
    }

    try {
        return JSON.parse(body) as PendoContextResponse;
    } catch (error) {
        const snippet = formatResponseSnippet(body);
        throw new Error(`Failed to parse JSON from Pendo context endpoint ${contextUrl}. Response preview: ${snippet}`, { cause: error });
    }
}

function pickString(...values: unknown[]): string | undefined {
    for (const value of values) {
        if (typeof value === 'string' && value.trim()) return value;
        if (Array.isArray(value)) {
            const first = value.find(item => typeof item === 'string' && item.trim());
            if (typeof first === 'string') return first;
        }
    }
    return undefined;
}

function pickBoolean(...values: unknown[]): boolean | undefined {
    for (const value of values) {
        if (typeof value === 'boolean') return value;
        if (typeof value === 'string') {
            const normalized = value.trim().toLowerCase();
            if (normalized === 'true') return true;
            if (normalized === 'false') return false;
        }
    }
    return undefined;
}

function pickEntityArray(value: unknown): PendoEntity[] | undefined {
    if (!Array.isArray(value)) return undefined;

    const values = value.flatMap<PendoEntity>(item => {
        if (typeof item === 'string' && item.trim()) {
            return [{ id: item, name: item }];
        }

        if (!item || typeof item !== 'object') {
            return [];
        }

        const entity = item as NamedEntity;
        const id = pickString(entity.id, entity.name);
        if (!id) return [];

        const name = typeof entity.name === 'string' ? entity.name : undefined;
        return [{ id, name }];
    });

    return values.length > 0 ? values : undefined;
}

function toClaimsRecord(authState: PendoAuthState): Record<string, unknown> {
    const claims = authState.account?.idTokenClaims;
    return claims && typeof claims === 'object' ? (claims as Record<string, unknown>) : {};
}

function toClaimMap(value: unknown): Record<string, string> {
    if (!Array.isArray(value)) return {};

    return value.reduce<Record<string, string>>((acc, item) => {
        if (!item || typeof item !== 'object') return acc;

        const claim = item as ClaimEntry;
        const type = typeof claim.type === 'string' ? claim.type : undefined;
        const claimValue = typeof claim.value === 'string' ? claim.value : undefined;

        if (type && claimValue) {
            acc[type] = claimValue;
        }

        return acc;
    }, {});
}

function buildVisitor(authState: PendoAuthState, response: PendoContextResponse): PendoVisitor | null {
    const claims = toClaimsRecord(authState);
    const currentUser = response.visitor ?? response.currentUser ?? {};
    const currentUserClaims = toClaimMap(currentUser.claims);

    const id = pickString(
        currentUser.sub,
        currentUser.id,
        currentUserClaims['sub'],
        currentUserClaims['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'],
        claims.sub,
        authState.account?.localAccountId,
        authState.account?.homeAccountId
    );
    if (!id) return null;

    const givenName = pickString(
        currentUser.givenName,
        currentUser.given_name,
        currentUserClaims['given_name'],
        currentUserClaims['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname'],
        claims.givenName,
        claims.given_name
    );
    const familyName = pickString(
        currentUser.familyName,
        currentUser.family_name,
        currentUserClaims['family_name'],
        currentUserClaims['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname'],
        claims.familyName,
        claims.family_name
    );
    const derivedName = [givenName, familyName].filter(Boolean).join(' ').trim();
    const name = pickString(currentUser.name, currentUserClaims['name'], claims.name) ?? (derivedName || undefined);

    return {
        id,
        email: pickString(
            currentUser.email,
            currentUser.emails,
            currentUser.username,
            currentUserClaims['email'],
            currentUserClaims['sign_in_name'],
            currentUserClaims['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'],
            claims.email,
            claims.emails,
            claims.preferred_username,
            authState.account?.username
        ),
        name,
        givenName,
        familyName,
        locale: pickString(currentUser.locale, currentUserClaims['locale'], claims.locale),
        isTenantAdmin: pickBoolean(currentUser.isTenantAdmin, currentUserClaims['isTenantAdmin'], claims.isTenantAdmin)
    };
}

function buildAccount(response: PendoContextResponse): PendoAccount | null {
    const tenantContext = response.account ?? response.tenantContext ?? {};
    const currentUser = response.currentUser ?? {};

    const id = pickString(tenantContext.id, tenantContext.tenantId, currentUser.tenantId);
    if (!id) return null;

    return {
        id,
        name: pickString(tenantContext.name),
        status: pickString(tenantContext.status),
        applications: pickEntityArray(tenantContext.applications),
        contentFeatures: pickEntityArray(tenantContext.contentFeatures),
        permission: pickString(tenantContext.permission),
        createdDate: pickString(tenantContext.createdDate)
    };
}

function buildIdentity(authState: PendoAuthState, response: PendoContextResponse): PendoIdentity | null {
    const visitor = buildVisitor(authState, response);
    const account = buildAccount(response);

    if (!visitor || !account) return null;

    return { visitor, account };
}

async function fetchPendoIdentity(authState: PendoAuthState): Promise<PendoIdentity | null> {
    const pendoConfig = env.pendo;
    if (!pendoConfig || !authState.isAuthenticated) return null;

    const { accessToken } = await authService.acquireToken({
        scopes: apiScopes,
        account: authState.account ?? undefined
    });
    const response = await fetch(pendoConfig.contextUrl, {
        headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${accessToken}`
        }
    });

    if (!response.ok) {
        throw new Error(`Failed to load Pendo context: ${response.status} ${response.statusText}`);
    }

    const json = await parsePendoContextResponse(response, pendoConfig.contextUrl);
    return buildIdentity(authState, json);
}

function getIdentityKey(identity: PendoIdentity | null): string | null {
    if (!identity) return null;
    return `${identity.visitor.id}::${identity.account.id}`;
}

export function PendoProvider({ authState, children }: PendoProviderProps) {
    const [contextState, setContextState] = useState<ContextState>({ identity: null, error: null });
    const pendoConfig = env.pendo;
    const pendoEnabled = Boolean(pendoConfig && pendoConfig.disabled !== true);
    const agentRef = useRef<PendoAgent | null>(null);
    const initializedRef = useRef(false);
    const lastIdentityKeyRef = useRef<string | null>(null);

    const canLoadPendo = useMemo(() => {
        if (!pendoEnabled || isAuthDisabled) return false;
        if (authState.isLoading) return false;
        return authState.isAuthenticated && Boolean(authState.accountId);
    }, [authState.accountId, authState.isAuthenticated, authState.isLoading, pendoEnabled]);

    useEffect(() => {
        if (!canLoadPendo) return;

        let cancelled = false;

        void (async () => {
            try {
                const identity = await fetchPendoIdentity({
                    isAuthenticated: authState.isAuthenticated,
                    account: authState.account
                });
                console.log('[Pendo] Fetched identity for visitor', identity);
                if (!cancelled) {
                    setContextState({ identity, error: null });
                }
            } catch (error) {
                if (!cancelled) {
                    setContextState({
                        identity: null,
                        error: error instanceof Error ? error : new Error(String(error))
                    });
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [authState.account, authState.accountId, authState.isAuthenticated, canLoadPendo]);

    useEffect(() => {
        const pendoConfig = env.pendo;
        const identity = contextState.identity;

        if (!canLoadPendo || !pendoConfig || !identity || !pendoConfig.apiKey) return;

        const identityKey = getIdentityKey(identity);
        if (!identityKey) return;

        void (async () => {
            if (!initializedRef.current && pendoConfig.apiKey) {
                const agent = (await initialize({
                    apiKey: pendoConfig.apiKey,
                    publicAppId: pendoConfig.publicAppId,
                    env: pendoConfig.env,
                    visitor: identity.visitor,
                    account: identity.account
                })) as PendoAgent;

                agentRef.current = agent;
                initializedRef.current = true;
                lastIdentityKeyRef.current = identityKey;
                return;
            }

            if (lastIdentityKeyRef.current !== identityKey) {
                agentRef.current?.identify(identity);
                lastIdentityKeyRef.current = identityKey;
            }
        })();
    }, [canLoadPendo, contextState.identity]);

    useEffect(() => {
        if (canLoadPendo) return;

        agentRef.current?.teardown?.();
        agentRef.current = null;
        initializedRef.current = false;
        lastIdentityKeyRef.current = null;
    }, [canLoadPendo]);

    useEffect(() => {
        if (!contextState.error) return;
        console.error('[Pendo] Unable to initialize Pendo', contextState.error);
    }, [contextState.error]);

    useEffect(() => {
        return () => {
            agentRef.current?.teardown?.();
            agentRef.current = null;
            initializedRef.current = false;
            lastIdentityKeyRef.current = null;
        };
    }, []);

    return <>{children}</>;
}

export { buildAccount, buildIdentity, buildVisitor, fetchPendoIdentity, parsePendoContextResponse };
